import {
  ChevronUpDownIcon,
  PlusCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { type SelectOption } from "@via/utils/interfaces.tsx";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { type Control, Controller } from "react-hook-form";
import Select, {
  type ControlProps,
  type MenuPlacement,
  type Options,
  components,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import "./select.css";

type FormatOptionLabelContext = {
  context: "menu" | "value";
};

const createHintOptionValue = "__create_hint__";

// Create hint option for showing create functionality upfront
const createHintOption: SelectOption = {
  value: createHintOptionValue,
  label: "Type to create new option",
};

interface SelectComponentProps {
  control?: Control<any>;
  rules?: Record<string, unknown>;
  name: string;
  enableClear?: boolean;
  readOnly?: boolean;
  isSearchable?: boolean;
  options: Options<SelectOption>;
  formatOptionLabel?: (
    option: SelectOption,
    context: FormatOptionLabelContext
  ) => React.ReactNode;
  placeholder?: string;
  noOptionsMessage?: string;
  isMulti?: boolean;
  value?: SelectOption | Options<SelectOption> | null;
  defaultValue?: SelectOption | Options<SelectOption> | null;
  onChange?: (selected: SelectOption | Options<SelectOption>) => void;
  isLoading?: boolean;
  menuPlacement?: MenuPlacement;
  menuPosition?: "absolute" | "fixed";
  menuShouldScrollIntoView?: boolean;
  prefixIcon?: React.ReactNode;
  onFetchMoreOptions?: () => void;
  isCreatable?: boolean;
  onCreateOption?: (inputValue: string) => void;
  onInputChange?: (inputValue: string) => void;
  showFullSelectedText?: boolean;
  onFocus?: () => void;
  showCreateHint?: boolean;
  maxMenuHeight?: number;
  /** 3G-3a: the id react-select puts on its text input, so a caller can move focus to the control. */
  inputId?: string;
  /** 3G-3a: when defined, the caller controls the menu (react-select `menuIsOpen`). */
  menuIsOpen?: boolean;
  /** 3G-3a: called after react-select closes the menu, whether after a choice, Escape or an outside click. */
  onMenuClose?: () => void;
}

interface CustomControlProps<
  OptionType,
  IsMulti extends boolean,
> extends ControlProps<OptionType, IsMulti> {
  prefixIcon?: React.ReactNode;
}

const CustomControl: React.FC<CustomControlProps<any, any>> = ({
  children,
  prefixIcon,
  ...props
}) => {
  return (
    <components.Control {...props}>
      {prefixIcon && <div className="ml-2 h-5 w-5">{prefixIcon}</div>}
      {children}
    </components.Control>
  );
};

const DropdownIndicator: React.FC<any> = props =>
  components.DropdownIndicator && (
    <components.DropdownIndicator {...props}>
      <ChevronUpDownIcon className="h-6 w-6 cursor-pointer text-gray-400 duration-200 ease-in-out hover:text-gray-500" />
    </components.DropdownIndicator>
  );

const ClearIndicator: React.FC<any> = props => (
  <components.ClearIndicator {...props}>
    <XMarkIcon className="h-5 w-5 cursor-pointer text-gray-400 duration-200 ease-in-out hover:text-gray-500" />
  </components.ClearIndicator>
);

// Custom option component for hint option
const CustomOption: React.FC<any> = props => {
  const { data } = props;

  if (data.value === createHintOptionValue) {
    return (
      <div className="px-3 py-2 text-sm text-gray-500 italic bg-gray-50 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <PlusCircleIcon className="h-5 w-5 text-gray-500" />
          <span>{data.label}</span>
        </div>
      </div>
    );
  }

  return <components.Option {...props} />;
};

const SelectComponent: React.FC<SelectComponentProps> = ({
  control,
  rules,
  name,
  enableClear = true,
  readOnly = false,
  isSearchable = true,
  options,
  formatOptionLabel,
  placeholder = "Select an option",
  noOptionsMessage = "No results available",
  isMulti = false,
  value: propValue,
  defaultValue = isMulti ? [] : null,
  onChange,
  isLoading = false,
  menuPlacement = "auto",
  menuPosition = "absolute",
  menuShouldScrollIntoView = true,
  prefixIcon,
  onFetchMoreOptions,
  isCreatable = false,
  onCreateOption,
  onInputChange,
  showFullSelectedText = false,
  onFocus,
  showCreateHint = true,
  maxMenuHeight,
  inputId,
  menuIsOpen,
  onMenuClose,
}) => {
  const [value, setValue] = useState<
    SelectOption | Options<SelectOption> | null
  >(defaultValue);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // 3G-3a: react-select measures its portal position off the control's DOM node, which is not
  // attached yet on the render that mounts it. A caller that mounts with menuIsOpen already true
  // would otherwise get a menu with nowhere computed to sit. One extra render right after mount,
  // only in that case, lets it pick up the now attached control and place the menu correctly.
  const mountedWithMenuOpenRef = useRef(menuIsOpen === true);
  const [, forceRerender] = useState(0);
  useEffect(() => {
    if (mountedWithMenuOpenRef.current) {
      forceRerender(count => count + 1);
    }
  }, []);

  useEffect(() => {
    if (propValue !== undefined) {
      setValue(propValue);
    }
  }, [propValue]);

  // 3G-3a: the report is keyed on react-select's own onMenuClose callback firing, a close
  // event, never on our internal isMenuOpen state or on onMenuClose's identity. isMenuOpen
  // stays false throughout when the caller controls the menu with menuIsOpen={true} (react-
  // select only calls its onMenuOpen prop from its own openMenu path, which a controlled open
  // never takes), so a state-transition effect would never fire in that case. react-select can
  // also call onMenuClose twice for one user-visible close (a select on a touch-capable
  // environment blurs the input right after a choice, which fires a second close), and it
  // calls the first of those before its own onChange. Counting closes and reacting to the
  // count, instead of calling the caller straight from react-select's callback, collapses any
  // such duplicate into the one close a caller actually cares about: two counter bumps from
  // one batched event still commit once, so the effect below still runs once, and it runs
  // after the choice that caused it. The mount guard is the counter's zero: it starts at 0 and only
  // react-select's close callback increments it, so 0 means no close has happened yet however many
  // times the effect runs. A "first run" ref would not hold under StrictMode, which in development
  // mounts, cleans up and re-runs every effect while refs persist, so the second run would report a
  // close that never happened.
  const onMenuCloseRef = useRef(onMenuClose);
  onMenuCloseRef.current = onMenuClose;
  const [closeSeq, setCloseSeq] = useState(0);
  useEffect(() => {
    if (closeSeq === 0) return;
    onMenuCloseRef.current?.();
  }, [closeSeq]);

  // Format create label function
  const formatCreateLabel = (inputValue: string): string => {
    const defaultMessage = "Create";
    return `${defaultMessage} "${inputValue}"`;
  };

  // Enhanced options with create hint
  const enhancedOptions = useMemo(() => {
    if (!isCreatable || !showCreateHint) return options;

    // Add hint option at the beginning
    return [createHintOption, ...options];
  }, [options, isCreatable, showCreateHint]);

  const handleOnChange = (selected): void => {
    setValue(selected);
    if (onChange) {
      onChange(selected);
    }
  };

  const handleInputChange = (inputValue: string): void => {
    if (onInputChange) {
      onInputChange(inputValue);
    }
  };

  const handleMenuScrollToBottom = (): void => {
    if (onFetchMoreOptions) {
      onFetchMoreOptions();
    }
  };

  const ControlWithPrefixIcon: React.FC<any> = props => (
    <CustomControl {...props} prefixIcon={prefixIcon} />
  );

  const memoizedComponents = useMemo(
    () => ({
      DropdownIndicator,
      ClearIndicator,
      Control: ControlWithPrefixIcon,
      Option: CustomOption,
    }),
    []
  );

  const commonProps = {
    isMulti,
    isClearable: enableClear,
    hideSelectedOptions: false,
    menuPlacement,
    menuPosition: menuPosition as "absolute" | "fixed",
    menuShouldScrollIntoView,
    options: enhancedOptions,
    formatOptionLabel,
    classNamePrefix: "select",
    noOptionsMessage: () => noOptionsMessage,
    placeholder,
    isLoading,
    isDisabled: (!isMenuOpen && isLoading) || readOnly,
    isSearchable,
    showFullSelectedText,
    components: memoizedComponents,
    onInputChange: handleInputChange,
    onMenuScrollToBottom: handleMenuScrollToBottom,
    onMenuOpen: () => {
      setIsMenuOpen(true);
    },
    onMenuClose: () => {
      setIsMenuOpen(false);
      setCloseSeq(n => n + 1);
    },
    inputId,
    ...(menuIsOpen !== undefined ? { menuIsOpen } : {}),
    formatCreateLabel: isCreatable ? formatCreateLabel : undefined,
    styles: {
      control: provided => ({
        ...provided,
        zIndex: 0,
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected
          ? "#007bff"
          : state.isFocused
            ? "#e6f0ff"
            : state?.data?.label === state?.selectProps?.value?.label
              ? "#94befc" // Highlight selected option when open
              : "white", // Default white for other options
      }),
      singleValue: provided => ({
        ...provided,
        whiteSpace: showFullSelectedText ? "normal" : "nowrap",
      }),
      menuPortal: base => ({ ...base, zIndex: 9999 }),
    },
    menuPortalTarget: document.body,
    onFocus,
    maxMenuHeight,
  };

  if (control) {
    return (
      <Controller
        name={name}
        rules={rules}
        control={control}
        defaultValue={defaultValue}
        render={({ field: { onChange, value, ...rest } }) => {
          const controlledProps = {
            ...rest,
            onChange: selected => {
              handleOnChange(selected);
              if (isMulti && Array.isArray(selected)) {
                onChange(selected.map(option => option.value));
              } else {
                if (selected === null) {
                  onChange(null);
                } else {
                  onChange((selected as SelectOption).value);
                }
              }
            },
            value:
              propValue ??
              (isMulti
                ? options.filter(option => value?.includes(option?.value))
                : options.find(option => option.value === value) || null),
            ...commonProps,
          };

          return isCreatable ? (
            <CreatableSelect
              {...controlledProps}
              onCreateOption={onCreateOption}
            />
          ) : (
            <Select {...controlledProps} />
          );
        }}
      />
    );
  }

  const unControlledProps = {
    value,
    onChange: handleOnChange,
    ...commonProps,
  };

  return isCreatable ? (
    <CreatableSelect {...unControlledProps} onCreateOption={onCreateOption} />
  ) : (
    <Select {...unControlledProps} />
  );
};

export default SelectComponent;
